import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AuthService } from '../../services/auth.service';
import { OnboardingService, WizardState } from '../../services/onboarding.service';
import { RealtimeService } from '../../services/realtime.service';
import { TicketMessage } from '../../services/support-tickets.service';

const SUPPORT_ROLE = 'support_codincloud';

/**
 * vd-onboarding-bubble — bulle de chat dédiée à la discussion d'onboarding
 * VENDEUR <-> équipe vendeur.site (validation du compte).
 *
 * Distincte de `vd-ai-bubble` (assistant IA) pour ne PAS alourdir/risquer ce
 * composant critique. Positionnée en bas-GAUCHE afin de ne jamais chevaucher
 * le FAB de l'assistant (bas-droite).
 *
 * UX demandée :
 *  - badge « non-lu » sur le FAB dès qu'un message support arrive ;
 *  - AUTO-OUVERTURE quand un nouveau message de l'équipe arrive (temps réel) ;
 *  - vue du fil de discussion (ticket onboarding) + composeur de réponse ;
 *  - lien vers le suivi complet (`/CompleteSignUp`).
 *
 * 100% additif / non régressif : ne rend RIEN si l'utilisateur n'est pas un
 * vendeur en cours d'onboarding (compte approuvé/hérité → `can_sell` true), ou
 * si l'API échoue. Le temps réel est best-effort (repli sur rechargement).
 */
@Component({
  selector: 'vd-onboarding-bubble',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="render()">
      <!-- Panneau -->
      <div class="ob-panel" *ngIf="open()" role="dialog" aria-label="Discussion onboarding">
        <header class="ob-head">
          <span class="material-icons-outlined ob-ic">support_agent</span>
          <div class="ob-head-txt">
            <strong>Équipe vendeur.site</strong>
            <span>{{ statusLabel() }}</span>
          </div>
          <button type="button" class="ob-x" (click)="toggle()" aria-label="Fermer">
            <span class="material-icons-outlined">close</span>
          </button>
        </header>

        <div class="ob-msgs" #scroll>
          <p class="ob-empty" *ngIf="!messages().length">
            Posez vos questions à l'équipe de validation. Nous vous répondons ici.
          </p>
          <div
            *ngFor="let m of messages(); trackBy: trackByMsg"
            class="ob-msg"
            [class.support]="isSupport(m)"
            [class.system]="m.kind === 'system'"
          >
            <div class="ob-bubble">{{ m.body }}</div>
            <time class="ob-time">{{ relativeTime(m.created_at) }}</time>
          </div>
        </div>

        <form class="ob-compose" (ngSubmit)="send()">
          <input
            type="text"
            [ngModel]="draft()"
            (ngModelChange)="draft.set($event)"
            name="ob-draft"
            placeholder="Écrire un message…"
            autocomplete="off"
            [disabled]="sending()"
          />
          <button type="submit" [disabled]="!canSend()" aria-label="Envoyer">
            <span class="material-icons-outlined">send</span>
          </button>
        </form>

        <button type="button" class="ob-foot" (click)="openWizard()">
          <span class="material-icons-outlined">open_in_new</span>
          Voir le suivi complet de mon inscription
        </button>
      </div>

      <!-- FAB -->
      <button
        type="button"
        class="ob-fab"
        [class.has-unread]="unread() > 0"
        (click)="toggle()"
        [attr.aria-label]="open() ? 'Fermer la discussion' : 'Discussion avec l\\'équipe vendeur.site'"
      >
        <span class="material-icons-outlined">{{ open() ? 'close' : 'forum' }}</span>
        <span class="ob-badge" *ngIf="!open() && unread() > 0">{{ unread() > 9 ? '9+' : unread() }}</span>
      </button>
    </ng-container>
  `,
  styles: [`
    .ob-fab {
      position: fixed; left: 24px; bottom: 24px; z-index: 9998;
      width: 56px; height: 56px; border-radius: 50%; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center; color: #fff;
      background: linear-gradient(135deg, #2563eb, #1e3a8a);
      box-shadow: 0 8px 24px rgba(30, 58, 138, .35); transition: transform .15s ease;
    }
    .ob-fab:hover { transform: translateY(-2px); }
    .ob-fab .material-icons-outlined { font-size: 26px; }
    .ob-fab.has-unread { animation: ob-pulse 1.8s ease-in-out infinite; }
    @keyframes ob-pulse {
      0%, 100% { box-shadow: 0 8px 24px rgba(30, 58, 138, .35); }
      50% { box-shadow: 0 8px 30px rgba(37, 99, 235, .7); }
    }
    .ob-badge {
      position: absolute; top: -4px; right: -4px; min-width: 20px; height: 20px; padding: 0 5px;
      border-radius: 10px; background: #ef4444; color: #fff; font-size: 11px; font-weight: 800;
      display: flex; align-items: center; justify-content: center; border: 2px solid #fff;
    }
    .ob-panel {
      position: fixed; left: 24px; bottom: 92px; z-index: 9998;
      width: 360px; max-width: calc(100vw - 32px); height: 480px; max-height: calc(100vh - 140px);
      display: flex; flex-direction: column; background: #fff; border-radius: 16px; overflow: hidden;
      box-shadow: 0 20px 50px rgba(15, 23, 42, .28); border: 1px solid #e2e8f0;
    }
    .ob-head {
      display: flex; align-items: center; gap: 10px; padding: 12px 14px; color: #fff;
      background: linear-gradient(135deg, #2563eb, #1e3a8a);
    }
    .ob-head .ob-ic { font-size: 24px; }
    .ob-head-txt { display: flex; flex-direction: column; line-height: 1.2; flex: 1; }
    .ob-head-txt strong { font-size: 14px; }
    .ob-head-txt span { font-size: 11.5px; opacity: .85; }
    .ob-x { background: transparent; border: none; color: #fff; cursor: pointer; padding: 4px; display: flex; }
    .ob-msgs { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; background: #f8fafc; }
    .ob-empty { color: #64748b; font-size: 13px; text-align: center; margin: auto; max-width: 240px; line-height: 1.5; }
    .ob-msg { display: flex; flex-direction: column; align-items: flex-end; max-width: 85%; align-self: flex-end; }
    .ob-msg.support { align-items: flex-start; align-self: flex-start; }
    .ob-msg.system { align-self: center; max-width: 95%; align-items: center; }
    .ob-bubble {
      padding: 9px 12px; border-radius: 14px; font-size: 13px; line-height: 1.45; white-space: pre-wrap;
      word-break: break-word; background: linear-gradient(135deg, #2563eb, #1e40af); color: #fff;
      border-bottom-right-radius: 4px;
    }
    .ob-msg.support .ob-bubble { background: #fff; color: #1e293b; border: 1px solid #e2e8f0; border-bottom-left-radius: 4px; border-bottom-right-radius: 14px; }
    .ob-msg.system .ob-bubble { background: #eef2ff; color: #4338ca; font-size: 12px; border: none; border-radius: 10px; text-align: center; }
    .ob-time { font-size: 10.5px; color: #94a3b8; margin-top: 3px; }
    .ob-compose { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #e2e8f0; background: #fff; }
    .ob-compose input {
      flex: 1; border: 1px solid #cbd5e1; border-radius: 10px; padding: 9px 12px; font-size: 13px; outline: none;
    }
    .ob-compose input:focus { border-color: #2563eb; }
    .ob-compose button {
      border: none; border-radius: 10px; width: 40px; cursor: pointer; color: #fff;
      background: linear-gradient(135deg, #2563eb, #1e3a8a); display: flex; align-items: center; justify-content: center;
    }
    .ob-compose button:disabled { opacity: .5; cursor: not-allowed; }
    .ob-foot {
      display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px;
      border: none; border-top: 1px solid #e2e8f0; background: #f1f5f9; color: #2563eb;
      font-size: 12.5px; font-weight: 600; cursor: pointer;
    }
    .ob-foot .material-icons-outlined { font-size: 16px; }
    @media (max-width: 480px) {
      .ob-panel { left: 8px; right: 8px; bottom: 86px; width: auto; }
      .ob-fab { left: 16px; bottom: 16px; }
    }
  `],
})
export class OnboardingBubbleComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly api = inject(OnboardingService);
  private readonly auth = inject(AuthService);
  private readonly realtime = inject(RealtimeService);
  private readonly router = inject(Router);

  readonly render = signal<boolean>(false);
  readonly open = signal<boolean>(false);
  readonly state = signal<WizardState | null>(null);
  readonly messages = signal<TicketMessage[]>([]);
  readonly draft = signal<string>('');
  readonly sending = signal<boolean>(false);
  readonly unread = signal<number>(0);

  readonly canSend = computed(() => this.draft().trim().length > 0 && !this.sending());

  @ViewChild('scroll') private scrollEl?: ElementRef<HTMLDivElement>;

  private subs: Subscription[] = [];
  private lastSupportMsgId = 0;
  private shouldScroll = false;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    const roles = this.auth.getRolesFromToken();
    if (!roles.includes('vendor') && !roles.includes('employee')) return;

    this.api.getState().subscribe({
      next: (st) => {
        this.state.set(st);
        // Bulle pertinente uniquement tant que la vente n'est pas débloquée
        // (statut explicite incomplete / pending_review / rejected / suspended).
        if (st.can_sell) return;
        this.render.set(true);
        this.loadThread(true);
        this.connectRealtime();
      },
      error: () => this.render.set(false),
    });
  }

  ngOnDestroy(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    this.subs.forEach((s) => {
      try { s.unsubscribe(); } catch { /* noop */ }
    });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.shouldScroll = false;
      const el = this.scrollEl?.nativeElement;
      if (el) {
        try { el.scrollTop = el.scrollHeight; } catch { /* noop */ }
      }
    }
  }

  private connectRealtime(): void {
    try {
      this.realtime.connect();
      this.subs.push(
        this.realtime.messages$.subscribe(() => this.onRealtimeSignal()),
        this.realtime.notifications$.subscribe(() => this.onRealtimeSignal()),
      );
    } catch {
      /* SSE indisponible : on reste en mode pull (ouverture/refresh). */
    }
  }

  /**
   * Tout signal serveur (message/notification) recharge l'état + le fil
   * (débounce léger). Si un message SUPPORT plus récent apparaît alors que le
   * panneau est fermé → badge non-lu + AUTO-OUVERTURE.
   */
  private onRealtimeSignal(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null;
      // Statut peut avoir changé (approuvé → on masque la bulle).
      this.api.getState().subscribe({
        next: (st) => {
          this.state.set(st);
          if (st.can_sell) {
            this.render.set(false);
            this.open.set(false);
          }
        },
        error: () => {},
      });
      this.refreshThread();
    }, 350);
  }

  private refreshThread(): void {
    this.api.getThread().subscribe({
      next: (t) => {
        const msgs = t.messages || [];
        this.messages.set(msgs);
        const latestSupport = [...msgs].reverse().find((m) => this.isSupport(m));
        const latestId = latestSupport ? latestSupport.id : 0;
        if (latestId > this.lastSupportMsgId) {
          this.lastSupportMsgId = latestId;
          if (this.open()) {
            // Panneau ouvert → la réponse est vue : on avance le marqueur.
            this.markRead(latestId);
            this.shouldScroll = true;
          } else {
            // Nouveau message équipe pendant que la bulle est fermée :
            // badge non-lu + AUTO-OUVERTURE (puis marqué lu car affiché).
            this.unread.update((n) => n + 1);
            this.open.set(true);
            this.markRead(latestId);
            this.shouldScroll = true;
          }
        } else if (this.open()) {
          this.shouldScroll = true;
        }
      },
      error: () => {},
    });
  }

  private loadThread(initial: boolean): void {
    this.api.getThread().subscribe({
      next: (t) => {
        const msgs = t.messages || [];
        this.messages.set(msgs);
        const latestSupport = [...msgs].reverse().find((m) => this.isSupport(m));
        const latestId = latestSupport ? latestSupport.id : 0;
        this.lastSupportMsgId = latestId;

        if (this.open()) {
          // Panneau déjà ouvert → tout est considéré lu.
          this.markRead(latestId);
          this.shouldScroll = true;
          return;
        }

        if (initial) {
          // RECONNEXION : surfacer les réponses support NON LUES (arrivées
          // pendant l'absence du vendeur). Sans ce calcul, la référence était
          // réinitialisée en silence → aucune notif/auto-ouverture au retour.
          // Le marqueur de lecture est persistant (localStorage par user).
          const lastRead = this.readMarker();
          const unreadCount = msgs.filter(
            (m) => this.isSupport(m) && m.id > lastRead,
          ).length;
          if (unreadCount > 0) {
            this.unread.set(unreadCount);
            this.open.set(true);
            this.markRead(latestId);
            this.shouldScroll = true;
          }
        }
      },
      error: () => {},
    });
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    if (next) {
      this.unread.set(0);
      this.markRead(this.lastSupportMsgId);
      this.loadThread(false);
      this.shouldScroll = true;
    }
  }

  // ── Marqueur de lecture persistant (par user) ───────────────────────────
  // Permet de détecter, à la (re)connexion, les réponses support jamais vues
  // par CE vendeur sur CE navigateur — indépendamment des sessions/onglets.
  private readMarkerKey(): string {
    let uid: number | null = null;
    try { uid = this.auth.getUserId(); } catch { uid = null; }
    return `vd-ob-lastread-${uid ?? 'anon'}`;
  }

  private readMarker(): number {
    try {
      const v = localStorage.getItem(this.readMarkerKey());
      const n = v ? parseInt(v, 10) : 0;
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  }

  private markRead(id: number): void {
    if (!id) return;
    try { localStorage.setItem(this.readMarkerKey(), String(id)); } catch { /* noop */ }
  }

  send(): void {
    const body = this.draft().trim();
    if (!body || this.sending()) return;
    this.sending.set(true);
    this.api.postMessage(body).subscribe({
      next: (res) => {
        this.sending.set(false);
        this.draft.set('');
        this.messages.update((arr) => [...arr, res.message]);
        this.shouldScroll = true;
      },
      error: () => this.sending.set(false),
    });
  }

  openWizard(): void {
    this.open.set(false);
    this.router.navigate(['/CompleteSignUp']);
  }

  isSupport(m: TicketMessage): boolean {
    return m.author_role === SUPPORT_ROLE;
  }

  statusLabel(): string {
    const s = this.state()?.vendor_status;
    if (s === 'pending_review') return 'Dossier en cours de validation';
    if (s === 'rejected') return 'Dossier à compléter';
    if (s === 'suspended') return 'Compte suspendu';
    return 'Finalisez votre inscription';
  }

  relativeTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso).getTime();
    if (Number.isNaN(d)) return '';
    const min = Math.round((Date.now() - d) / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const j = Math.round(h / 24);
    if (j < 7) return `il y a ${j} j`;
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  }

  trackByMsg = (_: number, m: TicketMessage) => m.id;
}
